import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { createCheckout } from '../lib/apiClient';

const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const payload = items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.qty }));
      const { checkout_url } = await createCheckout(payload, window.location.origin);
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (e) {
      toast({
        title: 'Checkout unavailable',
        description: 'We could not start checkout right now. Please try again shortly.',
      });
      setLoading(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      {/* Panel */}
      <aside
        className={`fixed top-0 right-0 z-[70] h-full w-full max-w-[420px] bg-fire-inksoft border-l border-fire-gold/20 flex flex-col transition-transform duration-400 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-fire-gold/15">
          <div className="flex items-center gap-2.5">
            <ShoppingBag className="w-5 h-5 text-fire-gold" />
            <h3 className="font-display text-xl text-fire-cream">Your Bag <span className="text-fire-cream/50 text-base">({count})</span></h3>
          </div>
          <button onClick={() => setOpen(false)} className="p-2 text-fire-cream/70 hover:text-fire-goldlight transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <ShoppingBag className="w-12 h-12 text-fire-gold/40 mb-4" strokeWidth={1.2} />
              <p className="text-fire-cream/60 font-cormorant text-xl mb-1">Your bag is empty</p>
              <p className="text-fire-cream/40 font-body text-sm mb-6">Wear your testimony.</p>
              <Link to="/shop" onClick={() => setOpen(false)} className="btn-outline-gold rounded-full px-6 py-2.5 text-sm font-semibold">
                Shop the Collection
              </Link>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((i) => (
                <li key={i.id} className="flex gap-3 card-dark rounded-xl p-3">
                  <img src={i.img} alt={i.name} className="w-16 h-16 rounded-lg object-cover bg-fire-ink" />
                  <div className="flex-1 min-w-0">
                    <p className="text-fire-cream font-body text-sm font-semibold leading-tight line-clamp-2">{i.name}</p>
                    <p className="text-fire-goldlight font-body text-sm mt-0.5">${i.price}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => updateQty(i.id, i.qty - 1)} className="w-6 h-6 rounded-full border border-fire-gold/30 text-fire-cream flex items-center justify-center hover:border-fire-gold">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-fire-cream font-body text-sm w-6 text-center">{i.qty}</span>
                      <button onClick={() => updateQty(i.id, i.qty + 1)} className="w-6 h-6 rounded-full border border-fire-gold/30 text-fire-cream flex items-center justify-center hover:border-fire-gold">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <button onClick={() => removeItem(i.id)} className="self-start p-1.5 text-fire-cream/40 hover:text-fire-maroon transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-fire-gold/15 px-6 py-5">
            <div className="flex items-center justify-between mb-4">
              <span className="font-cormorant text-lg text-fire-cream/80">Subtotal</span>
              <span className="font-display text-2xl text-gradient-gold">${subtotal}</span>
            </div>
            <button onClick={checkout} disabled={loading} className="btn-gold w-full rounded-full py-3.5 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70">
              {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>) : 'Secure Checkout'}
            </button>
            <p className="text-center text-fire-cream/40 text-[11px] font-body mt-3">Secure payment via Stripe · Test mode</p>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
